/**
 * @fileoverview Redux glue for premium feature gating.
 *
 * Bridges the pure entitlement logic (functions/entitlements/featureEntitlements)
 * to the store: lazily loads the plan catalog, exposes a selector, and provides
 * a `useFeature` hook that components use to gate premium UI.
 *
 * Everything here fails open — see featureEntitlements for the contract. Until
 * GET /plans is live, `useFeature(...).allowed` is always true, so this is inert.
 */
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchPlans } from 'store/reducers/thunks/planThunks';
import {
  selectPlanCatalogItems,
  selectPlanCatalogStatus,
} from 'store/reducers/uiReducer';
import { selectPlan } from 'store/reducers/selectors/userSelectors';
import { FEATURE_ROAD_NETWORK_COSTS, isFeatureAllowed } from 'functions/entitlements/featureEntitlements';

/**
 * Lazily fetch the plan catalog once. Safe to call from multiple mounted
 * components — it only dispatches while the catalog is idle. Mirrors
 * useEnsureExampleCatalog.
 */
export function useEnsurePlans() {
  const dispatch = useDispatch();
  const status = useSelector(selectPlanCatalogStatus);

  useEffect(() => {
    if (status === 'idle') {
      dispatch(fetchPlans());
    }
  }, [dispatch, status]);
}

/**
 * Selector: is the current user's plan entitled to `featureKey`? Fails open.
 *
 * @param {string} featureKey - One of the FEATURE_* constants.
 * @returns {(state: object) => boolean} A selector returning true if allowed.
 */
export const selectFeatureAllowed = (featureKey) => (state) => {
  const catalog = selectPlanCatalogItems(state);
  // An anonymous (signed-out) visitor has no resolved plan, so `selectPlan`
  // returns null. Treat them as the `free` tier for gating rather than failing
  // open: the backend models anonymous as `extends: free` (it can never exceed
  // free's entitlements), and a signed-in user always carries at least
  // `plan: 'free'` from login — so a null plan uniquely identifies an anonymous
  // visitor. Defaulting it to `free` closes the fail-open hole that let
  // signed-out users reach premium-only methods (e.g. road-network costs) with
  // no lock, without ever mislocking a signed-in (possibly Professional) user.
  const planName = selectPlan(state) || 'free';
  return isFeatureAllowed(catalog, planName, featureKey);
};

/**
 * Selector: may the current user use road-network (driving) travel costs, for
 * deciding which method a calculation uses? true, false, or undefined for "not
 * known yet".
 *
 * Differs from selectFeatureAllowed in one case. A signed-in user whose plan
 * this session hasn't loaded yet is unknown, not free: the login reducer writes
 * plan 'free' before /user_plan answers, and treating that as fact gave a
 * Professional user who had just signed in straight-line costs. Unknown keeps the
 * saved method, failing open like the other gates. A signed-out visitor is free,
 * as in selectFeatureAllowed.
 *
 * @param {object} state
 * @returns {boolean|undefined}
 */
export const selectRoadNetworkAccess = (state) => {
  const loginData = state?.inputData?.present?.loginData;
  if (loginData && loginData.loggedIn && !state?.ui?.userPlanLoaded) return undefined;
  return selectFeatureAllowed(FEATURE_ROAD_NETWORK_COSTS)(state);
};

/**
 * Hook: gate a premium feature. Ensures the plan catalog is loaded, then returns
 * whether the current user may use `featureKey`.
 *
 * `allowed` fails open (true) until the catalog loads and definitively locks the
 * feature, so premium UI stays unlocked wherever entitlements are unknown.
 *
 * @param {string} featureKey - One of the FEATURE_* constants.
 * @returns {{ allowed: boolean, loading: boolean }}
 */
export function useFeature(featureKey) {
  useEnsurePlans();
  const allowed = useSelector(selectFeatureAllowed(featureKey));
  const status = useSelector(selectPlanCatalogStatus);
  return { allowed, loading: status === 'idle' || status === 'loading' };
}
